import React from 'react';
import mfaEnableImgSrc from '../images/two-step-verification/mfa-enable.png';
import { gridSize } from '@atlaskit/theme';
import styled from '@emotion/styled';
import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import ButtonContainer from '../components/ButtonContainer';
import ButtonLink from '../components/ButtonLink';
import { FormattedMessage } from 'react-intl';
import {
  useButtonClickedEvent,
  useOperationalEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import messages from './MfaPromotePage.messages';
import { handleMfaPromoteUserSelection } from '../actions/mfa-promote-actions';
import { useForceRedirect } from '../utilities/hooks';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useLocation } from 'react-router-dom';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';
import { Box } from '@atlaskit/primitives';
import { usePasskeyEnrollmentTaskSession } from './MfaPromote/usePasskeyEnrollmentTaskSession';

const MfaPromoteContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const MfaImg = styled.img`
  height: ${gridSize() * 14}px;
  display: block;
  margin: 0 auto;
  padding: ${gridSize()}px 0;
`;

const ContentBody = styled.p`
  margin: ${gridSize()}px 0;
`;

const pageId = 'mfaPromotePage';

export async function securityScreenLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/security-screen/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa-promote'],
  });
}

export const MfaPromotePage = () => {
  const dispatch = useAppDispatch();
  const redirectTo = useAppSelector(state => state.mfaPromote.redirectTo);

  useForceRedirect({ redirectTo });
  usePageViewedEvent(pageId);
  useOperationalEvent({
    page: pageId,
    action: `loadMfaPromotePageSuccessful`,
    subject: 'sli',
  });

  const { search } = useLocation();

  const trackMfaContinueButtonClicked = useButtonClickedEvent(pageId, 'MfaPromoteContinue');
  const trackMfaLeanMoreLinkClicked = useButtonClickedEvent(pageId, 'MfaPromoteLearnMore');
  const trackDismissButtonClicked = useButtonClickedEvent(pageId, 'MfaPromoteDismiss');

  const onMfaContinueButtonClicked = () => {
    trackMfaContinueButtonClicked();
    dispatch(handleMfaPromoteUserSelection('edit_mfa', search));
  };

  const onDismissButtonClicked = () => {
    trackDismissButtonClicked();
    dispatch(handleMfaPromoteUserSelection('dismiss', search));
  };

  const { getUrlSearchEntriesForTaskSession } = usePasskeyEnrollmentTaskSession();

  const trackPasskeysLeanMoreLinkClicked = useButtonClickedEvent(
    pageId,
    'PasskeysPromoteLearnMore'
  );
  const trackPasskeysContinueButtonClicked = useButtonClickedEvent(
    pageId,
    'PasskeysPromoteContinue'
  );

  const onPasskeysContinueButtonClicked = () => {
    trackPasskeysContinueButtonClicked();

    dispatch(
      handleMfaPromoteUserSelection('edit_passkey', search, {
        additionalSearchParamsEntries: getUrlSearchEntriesForTaskSession(),
      })
    );
  };

  const hasPasskeys = useAppSelector(state => state.mfaPromote.hasPasskeys);

  return (
    <WhiteboxPage
      pageId="mfaPromotePage"
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.promoteMfaPasskeysHeader} />
        </Heading>
      }
      whiteboxFooter={
        <>
          <ButtonLink
            id="mfa-promote-learn-more"
            onClick={trackMfaLeanMoreLinkClicked}
            href="https://support.atlassian.com/atlassian-account/docs/manage-two-step-verification-for-your-atlassian-account/"
            target="_blank"
            rel="noopener noreferrer"
            shouldFitContainer>
            <FormattedMessage {...messages.promoteMfaLearnMore} />
          </ButtonLink>

          {!hasPasskeys && (
            <ButtonLink
              id="passkeys-promote-learn-more"
              onClick={trackPasskeysLeanMoreLinkClicked}
              href="https://support.atlassian.com/atlassian-account/docs/access-your-atlassian-account-with-a-passkey/"
              target="_blank"
              rel="noopener noreferrer"
              shouldFitContainer>
              <FormattedMessage {...messages.promotePasskeysLearnMore} />
            </ButtonLink>
          )}
        </>
      }>
      <MfaPromoteContainer>
        <MfaImg src={mfaEnableImgSrc} />
        <ContentBody>
          <FormattedMessage {...messages.promoteMfaMessageBody} />
        </ContentBody>
        <ButtonContainer key="mfa.promote.continue.container">
          <TallButton
            appearance="primary"
            id="mfa-promote-continue"
            type="submit"
            isFullWidth
            onClick={onMfaContinueButtonClicked}>
            <FormattedMessage {...messages.promoteMfaContinue} />
          </TallButton>
        </ButtonContainer>
        <ButtonContainer key="mfa.promote.dismiss.container">
          <TallButton
            appearance="default"
            id="mfa-promote-dismiss"
            testId="mfa-promote-dismiss-idf-testid"
            type="submit"
            isFullWidth
            onClick={onDismissButtonClicked}>
            <FormattedMessage {...messages.promoteMfaDismiss} />
          </TallButton>
        </ButtonContainer>

        {!hasPasskeys && (
          <Box paddingBlockStart="space.300">
            <ContentBody>
              <FormattedMessage {...messages.promotePasskeysMessageBody} />
            </ContentBody>
            <ButtonContainer key="passkeys.promote.continue.container">
              <TallButton
                appearance="default"
                id="passkeys-promote-continue"
                testId="mfa-promote-passkey-idf-testid"
                type="submit"
                isFullWidth
                onClick={onPasskeysContinueButtonClicked}>
                <FormattedMessage {...messages.promotePasskeysContinue} />
              </TallButton>
            </ButtonContainer>
          </Box>
        )}
      </MfaPromoteContainer>
    </WhiteboxPage>
  );
};

export default MfaPromotePage;
